

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Divider,
  Button,
  Alert,
  Paper,
} from "@mui/material";

/**
 * BotMessageSettings
 * A scaffold settings page to manage concierge bot messages and toggles.
 *
 * Props:
 * - initial: {
 *     enabled?: boolean,
 *     welcome?: string,
 *     away?: string,
 *     fallback?: string,
 *     handoffNote?: string
 *   }
 * - onSave: (payload) => Promise<void> | void
 * - onCancel: () => void
 */
export default function BotMessageSettings({ initial, onSave, onCancel }) {
  const defaults = useMemo(
    () => ({
      enabled: true,
      welcome: "Hi! I’m your virtual concierge. How can I help today?",
      away:
        "Our team is currently away. I’ll pass your message along and we’ll reply when we’re back online.",
      fallback:
        "I’m sorry, I didn’t quite catch that. Could you rephrase or tap one of the quick options?",
      handoffNote:
        "Handing you over to a human agent now — they’ll join this chat shortly.",
      ...(initial || {}),
    }),
    [initial]
  );

  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setForm(defaults);
  }, [defaults]);

  const handleChange = (key) => (e) => {
    const value =
      e?.target?.type === "checkbox" ? !!e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      // Minimal validation
      if (form.enabled && !form.welcome?.trim()) {
        throw new Error("Welcome message cannot be empty when the bot is enabled.");
      }
      const payload = {
        enabled: !!form.enabled,
        welcome: String(form.welcome || ""),
        away: String(form.away || ""),
        fallback: String(form.fallback || ""),
        handoffNote: String(form.handoffNote || ""),
      };
      if (onSave) {
        await onSave(payload);
      }
      setSavedAt(new Date());
    } catch (err) {
      setError(err?.message || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    setForm(defaults);
    setError("");
  };

  const SaveBar = (
    <Stack direction="row" spacing={1} sx={{ mt: 2 }} justifyContent="flex-end">
      <Button onClick={handleCancel} disabled={saving}>Cancel</Button>
      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? "Saving..." : "Save"}
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        ⚙️ Bot Messages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure automated concierge/AI messages used in the chat room.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {savedAt && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved at {savedAt.toLocaleTimeString()}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch checked={!!form.enabled} onChange={handleChange("enabled")} />
          }
          label="Enable concierge bot"
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -0.5 }}>
          When disabled, automated bot messages are not sent.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Welcome message
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={form.welcome}
          onChange={handleChange("welcome")}
          placeholder="Hi! I’m your virtual concierge. How can I help today?"
        />
        <Typography variant="caption" color="text.secondary">
          Sent when a new chat starts or the guest first messages.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Away / off-hours message
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={form.away}
          onChange={handleChange("away")}
          placeholder="We’re currently away. I’ll pass your message along..."
        />
        <Typography variant="caption" color="text.secondary">
          Used outside staffed hours or when no agents are available.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Fallback / didn’t understand
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={form.fallback}
          onChange={handleChange("fallback")}
          placeholder="I’m sorry, I didn’t quite catch that. Could you rephrase?"
        />
        <Typography variant="caption" color="text.secondary">
          When the bot cannot confidently answer.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Handoff note to human
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={form.handoffNote}
          onChange={handleChange("handoffNote")}
          placeholder="Handing you over to a human agent now..."
        />
        <Typography variant="caption" color="text.secondary">
          Sent when escalating the conversation to staff.
        </Typography>
      </Paper>

      {SaveBar}
    </Box>
  );
}